import React, { useState, useEffect, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import * as apiAssisant from "../api/assistant";
import AIChatResponse from "./AIChatResponse";
import VoiceInput from "./VoiceInput";

import * as apiProdotti from "../api/prodotti";
import * as apiMedici from "../api/medici";

import {
  Box,
  TextField,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Snackbar,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";

import { Send, Reddit, AttachFile } from "@material-ui/icons";
import { roles, getDatabaseNamePath } from "../config/config";

const ENABLE_REALTIME_TRANSCRIPTION = true;

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    maxWidth: "100vw",
    margin: "0 auto",
    position: "relative",
    // backgroundColor: '#ffffff',
    // borderRadius: '24px',
    // border: '2px solid #e2e8f0',
    // boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    // transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    // '&:focus-within': {
    //   borderColor: '#4f46e5',
    //   boxShadow: '0 8px 32px rgba(79, 70, 229, 0.15)',
    //   transform: 'translateY(-2px)',
    // },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      border: "none",
      borderRadius: "24px",
      paddingRight: "8px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
    "& .MuiInputBase-input": {
      // padding: '18px 24px',
      fontSize: "16px",
      lineHeight: "1.5",
      color: "#1e293b",
      "&::placeholder": {
        color: "#94a3b8",
        opacity: 1,
      },
    },
    "& .MuiInputBase-inputMultiline": {
      resize: "none",
    },
  },
  formControl: {
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "40%", // metà larghezza su schermi medi e grandi
    },
    margin: 8,
    // paddingLeft: 16,
    paddingBottom: 8,
    [theme.breakpoints.up("sm")]: {
      paddingBottom: 0,
    },
  },
  select: {
    height: 48,
    fontSize: 14,
    borderRadius: 16,
  },
  iconButton: {
    backgroundColor: "#f1f5f9",
    color: "#94a3b8",
    borderRadius: 16,
    padding: 10,
    margin: 4,
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: "#e2e8f0",
      transform: "scale(1.05)",
    },
    "&$iconButtonDisabled": {
      backgroundColor: "#f1f5f9",
      cursor: "not-allowed",
    },
  },
  iconButtonActive: {
    backgroundColor: "#4f46e5",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#4338ca",
    },
  },
  iconButtonDisabled: {},
}));

const AgentAssistant = () => {
  // COMPONENTE: Assistente conversazionale per agenti
  // Scopo: gestisce input utente, invoca l'API backend e mostra le risposte AI.
  // Stato chiave:
  // - messages: cronologia chat (user/ai)
  // - context: metadati correnti da passare al backend
  // - isLoading: indicatori di caricamento per UX
  // User redux
  
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const role = parseInt(user.idRole);

  const classes = useStyles(); // 👈 Inizializza gli stili

  const [isLoading, setIsLoading] = useState(false);

  const [uploadLoading, setUploadLoading] = useState(false);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [inputSnapshot, setInputSnapshot] = useState("");
  const [context, setContext] = useState({
    
  });
  const [lastResponseId, setLastResponseId] = useState(null);

  // Unica istanza Audio per riproduzione TTS
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);


  const handleFileUpload = async (event) => {
    // Upload di documenti che possono essere indicizzati lato server (RAG).
    const file = event.target.files[0];
    if (!file) return;

    //Salvo il riferimento all'input prima che venga "riciclato"
    const fileInput = event.target;

    const formData = new FormData();
    formData.append("file", file);

    setUploadLoading(true);
    try {
      const response = await fetch("http://192.168.1.13:8000/upload", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        // Salva solo il nome del file
        // Aggiungi il file alla lista
        const uploadedFiles = {
          id: Date.now(),
          name: file.name,
          size: file.size,
          time: Date.now(),
        };
        setUploadedFiles((prev) => [...prev, uploadedFiles]);

        setSnackbar({
          open: true,
          message: `Documento "${file.name}" caricato con successo! (${data.message})`,
          severity: "success",
        });
      } else {
        throw new Error(data.detail || "Errore sconosciuto");
      }
    } catch (error) {
      console.error("Errore upload:", error);
      console.error('Errore upload:', error);
      const uploadedFile = {
        id: Date.now(),
        name: file.name,
        size: file.size,
        time: Date.now(),
        error: true
      };
      setUploadedFiles(prev => [...prev, uploadedFile]);
      setSnackbar({
        open: true,
        message: `Errore nel caricamento: ${error.message}`,
        severity: "error",
      });
      // Puoi decidere di mostrare un errore in futuro
    } finally {
      setUploadLoading(false);
      fileInput.value = null; //Resetto il riferimento salvato precedentemente
    }
  };

  const handleSend = async (textOverride) => {
    // Se viene passato un testo specifico (es. da input vocale), usiamo quello.
    // Altrimenti usiamo lo stato 'input'. Attenzione: onClick passa l'evento come primo argomento.
    const textToSend = typeof textOverride === "string" ? textOverride : input;

    if (!textToSend.trim()) return;

    // --- 1. Aggiornamento Ottimistico UI ---
    // Aggiungiamo subito il messaggio dell'utente alla chat per una UX reattiva,
    // senza aspettare la risposta del server.
    const userMessage = { text: textToSend, sender: "user", context };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true); // Attiva l'indicatore di caricamento (spinner/robot)

    try {
      // --- 2. Preparazione Contesto Conversazione ---
      // Recuperiamo le ultime 10 risposte dell'IA per inviarle al backend.
      // Questo permette al modello di "ricordare" cosa è stato detto poco fa.
      // Nota: usiamo `messages` corrente + il nuovo `userMessage`.
      const fullMessages = [...messages, userMessage];
      const aiReplies = fullMessages.filter(m => m.sender === "ai");
      const recentReplies = aiReplies
        .slice(Math.max(aiReplies.length - 10, 0))
        .map((m, idx) => ({
          index: idx,
          text: m.text,
        }));

      const userAsks = fullMessages.filter(m => m.sender === "user");
      const recentAsks = userAsks
        .slice(Math.max(userAsks.length - 10, 0))
        .map((m, idx) => ({
          index: idx,
          text: m.text,
        }));
      
      // --- 3. Chiamata al Backend ---
      // Inviamo: messaggio, contesto corrente (es. prodotto), storico conversazione.
      const response = await apiAssisant.askToAgent(user.authToken, {
        userId: user.id,
        sessionId: user.authToken,
        previousResponseId: lastResponseId,
        message: userMessage.text,
        context,
        conversationReplies: recentReplies,
        conversationsAsks: recentAsks,
      });
      // --- 4. Gestione Risposta AI ---
      // La risposta contiene il testo, eventuali aggiornamenti di contesto, e l'audio TTS.
      const aiMessage = { text: response.response, responseId: response.responseId, sender: "ai", context: response.updatedContext || context };
      
      // Gestione Audio (TTS)
      // const aiSpeech = response.ai_speech;
      // playAudioFromBase64(aiSpeech.audio_base64);

      // console.log(aiSpeech['audio_base64']);
  
      // Aggiorniamo la chat con la risposta dell'IA
      setMessages((prev) => [...prev, aiMessage]);

      // Se l'IA ha rilevato un cambio di contesto (es. nuovo prodotto), aggiorniamo lo stato globale
      if (response.updatedContext) {
        setContext(response.updatedContext);
      }

      if (response.responseId) {
        setLastResponseId(response.responseId);
      }
    } catch (error) {
      console.error("Errore API:", error);
      // Feedback visivo in caso di errore
      const errorMessage = { text: "Spiacenti, si è verificato un errore. Riprova.", sender: "ai", context };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false); // Spegniamo lo spinner
    }
  };

  const playAudioFromBase64 = async (base64String) => {
    // Riproduce l'audio TTS ricevuto dal backend (base64 -> Blob -> Audio)
    if (!base64String) return;

    // Pausa e pulizia dell'istanza audio corrente (blocca audio precedente)
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch (_) {}
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    } else {
      audioRef.current = new Audio();
    }

    // Decodifica Base64 → Uint8Array
    const binaryString = atob(base64String);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Crea Blob e URL
    const blob = new Blob([bytes], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);
    audioUrlRef.current = url;

    // Configura la singola istanza Audio
    const audio = audioRef.current;
    audio.src = url;
    audio.preload = 'auto';

    const cleanup = () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      audio.removeEventListener('ended', cleanup);
      audio.removeEventListener('error', cleanup);
    };
    audio.addEventListener('ended', cleanup);
    audio.addEventListener('error', cleanup);

    // Riproduci e gestisci il permesso
    try {
      await audio.play();
      console.log('▶️ Audio in riproduzione');
    } catch (err) {
      console.warn('⚠️ Impossibile riprodurre automaticamente l’audio:', err);
    }
  };

  const handleKeyPress = (e) => {
    // Invio rapido: Enter invia, Shift+Enter va a capo
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSpeechStart = () => {
    // Salva l'input corrente prima di iniziare la dettatura
    setInputSnapshot(input);
  };

  const handleSpeechInterim = (text) => {
    // Aggiorna l'input in tempo reale con il testo parziale
    const prefix = inputSnapshot ? inputSnapshot + " " : "";
    setInput(prefix + text);
  };

  const handleSpeechResult = (text) => {
    // Combina l'input originale con il risultato finale
    const prefix = inputSnapshot ? inputSnapshot + " " : "";
    const fullText = prefix + text;
    
    setInput(fullText);
    // Invia direttamente quando il riconoscimento vocale termina
    handleSend(fullText);
  };

  return (
    <Box
      sx={{
        height: "80vh", // limita l'altezza per evitare scroll della pagina
        display: "flex",
        flexDirection: "column",
        minHeight: 0, // consente al figlio scrollabile di funzionare correttamente
        overflow: "hidden", // confina lo scroll all'area messaggi
        backgroundColor: "#f5f2f5",
        position: "relative",
        borderRadius: "20px",
      }}
    >
      {/* Header con context tags */}
      <Box
        sx={{
          padding: "16px 24px",
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          borderRadius: "10px",
        }}
      >        
      </Box>

      {/* Messages area */}
      <Box
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain", // evita lo scroll della pagina quando si raggiunge il bordo
          padding: "20px 24px",
          paddingBottom: "120px", // Spazio per l'input box fissa
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f5f9",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#cbd5e1",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#94a3b8",
          },
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              color: "#64748b",
            }}
          >
            <Reddit sx={{ fontSize: "64px", color: "#cbd5e1", marginBottom: "16px" }} />
            <Typography variant="h6" sx={{ marginBottom: "8px", color: "#475569" }}>
              Ciao! Come posso aiutarti?
            </Typography>
            <Typography variant="body2">Chiedimi suggerimenti su prodotti, medici, campioni e altro ancora</Typography>
          </Box>
        )}
        
        {messages.map((msg, index) => (
          <AIChatResponse key={index} text={msg.text} isUser={msg.sender === "user"} isLoading={false} context={msg.context} />
        ))}

        {/* Mostra lo spinner se si sta caricando */}
        {isLoading && (
          <AIChatResponse text="" isUser={false} context={context} isLoading={true} />
        )}
      </Box>

      {/* Fixed input area */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "20px 24px",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(16px)",
          borderTop: "1px solid #e2e8f0",
          zIndex: 1000,
          borderRadius: "0 0 20px 20px",
        }}
      >
        <Paper elevation={0} className={classes.paperContainer}>
          <Box display="flex" flexDirection="column" width="100%">
            {/* Righe delle Select */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              width="100%"
              gap={8}
              sx={{ paddingBottom: "8px" }}
            >
            </Box>

            {/* Mostra il chip con il nome del file caricato */}
            {uploadedFiles.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  marginBottom: "8px",
                }}
              >
                {uploadedFiles.map((file) => (
                  <Chip
                    key={file.name}
                    icon={<AttachFile style={{ color: file.error ? '#ef4444' : '#4f46e5' }} />}
                    label={file.name}
                    size="small"
                    onDelete={() => { setUploadedFiles(prev => prev.filter(f => f.id !== file.id)) }}
                    sx={{
                      backgroundColor: file.error ? '#fee2e2' : '#f0f9ff',
                      borderColor: file.error ? '#fca5a5' : '#bae6fd',
                      color: file.error ? '#b91c1c' : '#0284c7',
                      fontWeight: 500,
                      fontSize: '12px',
                      height: '28px',
                      animation: 'fadeIn 0.3s ease-in-out',
                      '& .MuiChip-icon': {
                        marginLeft: '4px',
                        marginRight: '0',
                      }
                    }}
                  />
                ))}
              </Box>
            )}

            {/* TextField sotto */}
            <TextField
              fullWidth
              multiline
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Chiedimi suggerimenti su prodotti, medici, campioni..."
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" style={{ alignSelf: "flex-end", marginBottom: "8px" }}>
                    {/* Bottone graffetta per upload */}
                    {role === roles.developer && (
                      
                    <IconButton component="label" className={classes.iconButton} size="small" style={{ padding: 10 }}>
                      <AttachFile style={{ fontSize: 20, color: "#64748b" }} />
                      <input type="file" hidden accept=".pdf,.docx,.txt" onChange={handleFileUpload} />
                    </IconButton>
                    )}
                    
                    {/* Componente per input vocale */}
                    <VoiceInput 
                      onSpeechResult={handleSpeechResult} 
                      onSpeechInterim={handleSpeechInterim}
                      onSpeechStart={handleSpeechStart}
                      enableRealtime={ENABLE_REALTIME_TRANSCRIPTION}
                    />

                    <IconButton
                      onClick={handleSend}
                      disabled={!input.trim()}
                      classes={{
                        root: input.trim() ? `${classes.iconButton} ${classes.iconButtonActive}` : classes.iconButton,
                        disabled: classes.iconButtonDisabled,
                      }}
                    >
                      <Send style={{ fontSize: 20 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className={classes.textField}
            />
            {/* Snackbar per messaggi di upload */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              message={snackbar.message}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
          </Box>
        </Paper>

        {/* Hint text */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: "#94a3b8",
            marginTop: "12px",
            fontSize: "12px",
          }}
        >
          Premi Invio per inviare • Shift + Invio per andare a capo
        </Typography>
      </Box>
    </Box>
  );
};

export default AgentAssistant;
